import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { apiUrl } from '@app/app.constants';
import { File as PortalFile } from '@app/core/interfaces/download/file';
import { PortalMedia } from '@app/core/interfaces/portal-media';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { withoutEntry } from '@app/utils/common';
import { dataSizeUnitToBytes } from '@app/utils/data-size-unit-to-bytes';
import { FileChecker, RejectedFile, RejectedFiles } from '@app/utils/file-checker';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'portal-file-drop-area',
  templateUrl: './file-drop-area.component.html',
  styles: [],
  providers: [ngValueAccessor(FileDropAreaComponent)],
  host: {
    class: 'file-drop-area',
  },
  standalone: false,
})
export class FileDropAreaComponent extends ReactiveComponent implements OnInit, OnChanges {
  /**
   * accepted MIME types
   */
  @Input()
  accept: string;

  /**
   * Optional translation key to describe accepted files.
   */
  @Input()
  acceptInfo?: string;

  /**
   * Description that is shown if a given file is not accepted because of its type
   */
  @Input()
  acceptDescription?: string;

  @Input()
  clearable?: boolean;

  currentFile = input<PortalFile | PortalMedia>();

  @Input()
  clearFiles?: Observable<any>;

  @Input()
  dropOnly: boolean = false;

  @Input('maxFileSize')
  set _maxFileSize(value: number | string) {
    this.maxFileSize = dataSizeUnitToBytes(value);
  }

  @Input()
  multiple: boolean = false;

  noAcceptDescription = input(false, { transform: coerceBooleanProperty });

  @Input()
  noBorder: boolean = false;

  @Input()
  preview?: 'image';

  @Input()
  progress: number | null = null;

  @Output()
  clear = new EventEmitter<void>();

  @Output()
  dropped = new EventEmitter<File[]>();

  @Output()
  droppedRejected = new EventEmitter<RejectedFile[]>();

  @ViewChild('file')
  fileInput!: ElementRef<HTMLInputElement>;

  activeFile = signal<PortalFile | PortalMedia | null>(null);
  imageUrl = computed(() =>
    this.activeFile() != null ? apiUrl(`/reseller/media/${this.activeFile().id}/download`) : null
  );

  active: boolean = false;
  files: File[] = [];
  maxFileSize: number | null = null;
  rejectedFiles: RejectedFiles | null = null;

  private fileChecker = new FileChecker();

  ngOnInit() {
    if (this.clearFiles != null) {
      this.clearFiles.pipe(untilDestroyed(this)).subscribe(() => {
        this.files = [];
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    let accept: string | null = null;
    let maxFileSize: number | null = null;

    if ('accept' in changes) {
      accept = changes.accept.currentValue;
    }

    if ('_maxFileSize' in changes) {
      maxFileSize = dataSizeUnitToBytes(changes._maxFileSize.currentValue);
    }

    if ('currentFile' in changes) {
      this.activeFile.set(changes.currentFile.currentValue);
    }

    this.fileChecker = new FileChecker({
      accept,
      maxFileSize,
    });
  }

  removeCurrentFile(): void {
    this.activeFile.set(null);
    this.clear.emit();
  }

  removeFile(file: File): void {
    this.files = withoutEntry(this.files, file, (a, b) => a === b);

    this.emit();
  }

  removeRejectedFile(file: RejectedFile): void {
    const files = withoutEntry(this.rejectedFiles.files, file);

    if (files.length > 0) {
      this.rejectedFiles.files = files;
    } else {
      this.rejectedFiles = null;
    }

    this.droppedRejected.emit(files);
  }

  setActive(active: boolean): void {
    this.active = active;
    this.onTouched();
  }

  setFile(files: File[]): void {
    this.active = false;
    this.rejectedFiles = null;

    if (this.multiple) {
      this.files.push(...files);
    } else {
      this.files = files;
    }

    this.emit();

    if (this.dropOnly) {
      this.files = [];
    }
  }

  setFileFromInput(files: FileList): void {
    const { acceptedFiles, rejectedFiles } = this.fileChecker.check(files);

    this.fileInput.nativeElement!.value = null;

    if (acceptedFiles.length > 0) {
      this.setFile(acceptedFiles);
    }

    if (rejectedFiles.length > 0) {
      this.setRejectedFiles({
        accept: this.acceptDescription ?? this.accept,
        files: rejectedFiles,
        maxFileSize: this.maxFileSize,
      });
      this.droppedRejected.emit(rejectedFiles);
    }
  }

  setRejectedFiles(rejectedFiles: RejectedFiles | null): void {
    this.active = false;
    this.files = [];
    this.rejectedFiles = rejectedFiles;

    this.onChange(this.multiple ? [] : null);
  }

  writeValue(file: File | File[] | null): void {
    if (Array.isArray(file)) {
      this.files = file;
    } else if (file != null) {
      this.files = [file];
    } else {
      this.files = [];
    }
  }

  private emit(): void {
    this.dropped.emit(this.files);

    if (this.multiple) {
      this.onChange(this.files);
    } else {
      this.onChange(this.files[0] ?? null);
    }
  }
}
